import { color } from 'chart.js/helpers';
import { contextMenu } from 'react-contexify';

import createInfoboxMarkup from '../../../infobox/createInfoboxMarkup';
import {
  COMMON_CHART_FONT_STYLES,
  getChartBaseColors,
  getChartDataLabelColor,
  getChartFontColor,
  getChartGridColor,
  getTrendColor,
} from '../../../../utils/chart';
import { VERTICAL_BARS_CONTEXT_MENU } from './SonarVerticalBars';

export const createData = ({ reverseTrendGroup, theme, data, dataKey, showTrend, showScore }) => {
  const BASE_COLORS = getChartBaseColors(theme);

  const datasets = [];
  const labels = data.map(({ label, domain }) => label || domain);
  const scores = data.map((el) => el[dataKey].score);
  const trends = data.map((el) => el[dataKey].trend);

  const getColor = (value) => {
    return value < 0 ? BASE_COLORS.RED : BASE_COLORS.GREEN;
  };

  const gettingTrendColor = (value, score) => {
    return showScore
      ? getTrendColor({
          trend: value,
          score: score,
          isReversed: reverseTrendGroup,
          theme,
        })
      : getColor(value);
  };

  showScore &&
    datasets.push({
      label: 'Score',
      backgroundColor: showTrend
        ? color(BASE_COLORS.GREEN).alpha(0.9).rgbString()
        : scores.map((value) => color(getColor(value)).alpha(0.9).rgbString()),
      borderWidth: 0,
      data: scores,
    });

  showTrend &&
    datasets.push({
      label: 'Trend',
      backgroundColor: trends.map((value, i) => {
        return color(gettingTrendColor(value, scores?.[i])).alpha(0.9).rgbString();
      }),
      borderWidth: 0,
      data: trends,
    });

  return {
    labels,
    datasets,
  };
};

export const createOptions = ({ theme, title, data, dataKey, customTooltip, showTrend, showScore }) => {
  const gridColor = getChartGridColor(theme);
  const fontColor = getChartFontColor(theme);

  return {
    responsive: true,
    maintainAspectRatio: false,
    borderRadius: 8,
    elements: {
      bar: {
        borderRadius: 5,
      },
    },
    ...(showScore && showTrend
      ? {
          interaction: {
            mode: 'index',
            axis: 'x',
          },
        }
      : {}),
    plugins: {
      legend: {
        display: !!(showTrend && showScore),
        position: 'top',
        labels: {
          color: fontColor,
          usePointStyle: true,
          boxWidth: 8,
          font: COMMON_CHART_FONT_STYLES,
          padding: 20,
        },
      },
      title: {
        display: !!title,
        text: title,
        color: fontColor,
        font: COMMON_CHART_FONT_STYLES,
      },
      tooltip: {
        intersect: false,
        enabled: !customTooltip,
        external: function (context) {
          customTooltip &&
            getTooltipsCustomConfig(context, {
              data,
              dataKey,
              showTrend,
              showScore,
            });
        },
      },
      datalabels: {
        display: false,
      },
    },

    scales: {
      x: {
        offset: true,
        position: 'bottom',
        ticks: {
          color: function ({ tick: { value } }) {
            return getChartDataLabelColor({ inFocus: data[value].inFocus, myCompany: data[value].myCompany, theme });
          },
          font: COMMON_CHART_FONT_STYLES,
          padding: 8,
          callback: function (value) {
            const label = this.getLabelForValue(value);
            const limit = 19;
            return '     ' + (label.length <= limit ? label : label.substring(0, limit - 2) + '...');
          },
        },
        grid: {
          display: true,
          drawTicks: false,
          color: gridColor,
        },
      },
      y: {
        offset: true,
        position: 'left',
        ticks: {
          color: fontColor,
          font: COMMON_CHART_FONT_STYLES,
          padding: 8,
        },
        grid: {
          display: true,
          drawTicks: false,
          color: gridColor,
        },
      },
    },
    events: ['mousemove', 'contextmenu', 'mouseout'],
    onClick: (event, elements) => {
      if (event.native.button === 2 && elements.length) {
        event.native.preventDefault();
        const clickedBar = elements[0];
        const index = clickedBar.index;
        const domain = data[index].domain;

        if (domain) {
          contextMenu.show({
            id: VERTICAL_BARS_CONTEXT_MENU,
            event: event.native,
            props: {
              domain,
            },
          });
        }
      }
    },
  };
};

const getTooltipsCustomConfig = (context, { data, dataKey, showTrend, showScore }) => {
  const { chart, tooltip: tooltipModel } = context;
  // Tooltip Element
  let tooltipEl = document.getElementById('chartJsFancyColumnsNewTooltip');

  // Create element on first render
  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'chartJsFancyColumnsNewTooltip';
    document.body.appendChild(tooltipEl);
  }

  // Hide if no tooltip
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  // Set caret Position
  tooltipEl.classList.remove('above', 'below', 'no-transform', 'center');
  if (tooltipModel.yAlign) {
    tooltipEl.classList.add(tooltipModel.yAlign);
  } else {
    tooltipEl.classList.add('no-transform');
  }

  // Set Content
  const index = tooltipModel.dataPoints[0].dataIndex;
  const label = tooltipModel.dataPoints[0].label;

  const domain = data[index].domain || '';
  const companyName = data[index].companyName || '';

  const baseColor = tooltipModel.labelColors[0].backgroundColor;
  const colorScore = tooltipModel.labelColors[0].backgroundColor;
  const colorTrend = tooltipModel.labelColors[1]?.backgroundColor;

  const tooltipTitle = label;
  const tooltipSubtitle = companyName ? domain : '';

  const records = [];

  showScore &&
    !isNaN(data[index][dataKey].score) &&
    records.push({
      name: 'Score',
      value: data[index][dataKey].score,
      color: colorScore || baseColor,
    });
  showTrend &&
    !isNaN(data[index][dataKey].trend) &&
    records.push({
      name: 'Trend',
      value: data[index][dataKey].trend,
      color: colorTrend || baseColor,
    });

  if (tooltipModel.body) {
    tooltipEl.innerHTML = createInfoboxMarkup({
      title: tooltipTitle,
      subTitle: tooltipSubtitle,
      records,
    });
  }

  // Get the canvas bounds
  const canvasBounds = chart.canvas.getBoundingClientRect();

  // Calculate tooltip position
  const positionX = canvasBounds.left + window.scrollX + tooltipModel.caretX;
  const positionY = canvasBounds.top + window.scrollY + tooltipModel.caretY;

  // Determine whether the bar value is positive or negative
  const isPositiveBar =
    (showScore && data[index][dataKey].score >= 0) || (showTrend && data[index][dataKey].trend >= 0);

  // Set vertical position based on bar value
  if (isPositiveBar) {
    tooltipEl.style.transform = 'translate(-50%, -100%)';
  } else {
    tooltipEl.style.transform = 'translate(-50%, 0)';
  }

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1;
  tooltipEl.style.pointerEvents = 'none';
  tooltipEl.style.position = 'absolute';
  tooltipEl.style.transition = 'all .5s ease';
  tooltipEl.style.left = positionX + 'px';
  tooltipEl.style.top = positionY + 'px';
  tooltipEl.style.font = tooltipModel.options.bodyFont.string;
  tooltipEl.style.padding = tooltipModel.options.padding + 'px ' + tooltipModel.options.padding + 'px';
};
